import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Copy, Download, Volume2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/lib/languages';
import { supabase } from '@/integrations/supabase/client';

const TextTranslation = () => {
  const [sourceLanguage, setSourceLanguage] = useState<string>('auto');
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>('en');
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const wordCount = sourceText.trim().split(/\s+/).filter(Boolean).length;

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error('Please enter your text to get started!');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: sourceText,
          sourceLanguage: sourceLanguage === 'auto' ? 'auto-detect' : sourceLanguage,
          targetLanguage,
        },
      });

      if (error) throw error;

      setTranslatedText(data.translatedText);
      toast.success('Translation complete!');
    } catch (error: any) {
      console.error('Translation error:', error);
      const errorMsg = error.message || error.error || JSON.stringify(error);
      if (errorMsg.toLowerCase().includes('trial')) {
        toast.error('Your trial has ended. Subscribe to continue using the translator.');
      } else {
        toast.error('Something went wrong. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(translatedText);
    toast.success('Copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([translatedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'translation.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded successfully!');
  };

  const handleSpeak = () => {
    if ('speechSynthesis' in window) {
      setSpeaking(true);
      const utterance = new SpeechSynthesisUtterance(translatedText);
      utterance.lang = targetLanguage;
      utterance.onend = () => setSpeaking(false);
      speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech not supported in your browser');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label>Source Language</Label>
            <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Text</Label>
              <span className="text-sm text-muted-foreground">{wordCount} words</span>
            </div>
            <Textarea
              placeholder="Enter text here to translate..."
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              className="min-h-[200px] resize-none"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Target Language</Label>
            <Select value={targetLanguage} onValueChange={(value) => setTargetLanguage(value as LanguageCode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Translation</Label>
            <div className="relative">
              <Textarea
                value={translatedText}
                readOnly
                placeholder="Translation will appear here..."
                className="min-h-[200px] resize-none bg-muted/30"
              />
              {translatedText && (
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button size="icon" variant="ghost" onClick={handleCopy}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={handleSpeak} disabled={speaking}>
                    <Volume2 className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={handleDownload}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Button onClick={handleTranslate} disabled={loading} size="lg" className="w-full">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Translating...
          </>
        ) : (
          'Translate'
        )}
      </Button>
    </div>
  );
};

export default TextTranslation;
